import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const { setMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from ? `${location.state.from.pathname}${location.state.from.search || ""}` : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setMode("studio"); }, [setMode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back! 🎮");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#05060a] text-white">
      <div className="hidden lg:flex flex-1 relative items-center justify-center overflow-hidden">
        <img src="/brand/gv-hero-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05060a]/20 via-[#05060a]/10 to-[#05060a]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05060a] via-transparent to-[#05060a]/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 85% at 32% 42%, transparent 28%, rgba(5,6,10,0.9) 100%)" }} />
        <div className="relative z-10 text-center px-10">
          <img src="/brand/gameview-logo-blend.png" alt="GameView Studio" className="w-72 mx-auto drop-shadow-[0_8px_40px_rgba(59,158,255,0.35)]" data-testid="login-brand-logo" />
          <p className="mt-5 text-white/60 max-w-sm mx-auto">Speak it. Play it. Share it. Your imagination, instantly playable.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-rise">
          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" /></div>
          <h1 className="font-display text-3xl font-bold mb-1">Welcome back</h1>
          <p className="text-white/50 mb-8 text-sm">Log in to keep building games.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email or username</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" required className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-full py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-white/50 text-center">
            <Link to="/forgot-password" className="text-[#3b9eff] font-semibold" data-testid="goto-forgot">Forgot your password?</Link>
          </p>
          <p className="mt-4 text-sm text-white/50 text-center">
            New here? <Link to="/register" state={location.state} className="text-[#3b9eff] font-semibold" data-testid="goto-register">Create an account</Link>
          </p>
          <p className="mt-2 text-sm text-white/50 text-center">
            Joining a class? <Link to="/join" className="text-[#3b9eff] font-semibold" data-testid="goto-join">Enter a class code</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
